// Analytics consent + consent-gated Google Tag Manager (docs/LAUNCH_SKILLS_CONTRACT.md "Consent").
//
// The site's own analytics are cookieless, so consent only matters when an admin sets a GTM container
// (GET /api/site-config `analytics.gtmId`). Rules:
//   - localStorage `icd_consent` = { analytics: boolean, ts } (the server-rendered blog reads the same key).
//   - Google Consent Mode v2 defaults to denied; analytics_storage is granted only after "Accept".
//   - GTM (https://www.googletagmanager.com/gtm.js?id=…) loads only with consent, never on /admin* or /dashboard*.
//   - A browser sending Global Privacy Control counts as declined, and gets no banner.

export const CONSENT_KEY = 'icd_consent';
export const CONSENT_EVENT = 'icd-consent-change';
export const CONSENT_OPEN_EVENT = 'icd-consent-open';

const GTM_ID_RE = /^GTM-[A-Z0-9]{4,12}$/;
const PRIVATE_PATH_RE = /^\/(admin|dashboard)(\/|$)/;

export function hasGpc() {
  try {
    return typeof navigator !== 'undefined' && navigator.globalPrivacyControl === true;
  } catch {
    return false;
  }
}

/** `{ analytics: boolean, ts }` or null when the visitor hasn't chosen yet. */
export function readConsent() {
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (!v || typeof v.analytics !== 'boolean') return null;
    return { analytics: v.analytics, ts: Number(v.ts) || 0 };
  } catch {
    return null;
  }
}

/** True only when the visitor accepted analytics and isn't sending GPC. */
export function analyticsAllowed() {
  if (hasGpc()) return false;
  const c = readConsent();
  return Boolean(c && c.analytics);
}

export function isPrivatePath(pathname) {
  return PRIVATE_PATH_RE.test(String(pathname || ''));
}

export function normalizeGtmId(id) {
  const v = String(id || '').trim().toUpperCase();
  return GTM_ID_RE.test(v) ? v : '';
}

function gtag() {
  window.dataLayer = window.dataLayer || [];
  // Consent Mode reads the `arguments` object, not an array.
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

let defaultsSet = false;

/** Consent Mode v2 defaults: everything denied until the visitor accepts (ads stay denied). */
export function setConsentDefaults() {
  if (defaultsSet || typeof window === 'undefined') return;
  defaultsSet = true;
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
    security_storage: 'granted',
    wait_for_update: 500,
  });
}

function updateConsentMode(analytics) {
  setConsentDefaults();
  gtag('consent', 'update', { analytics_storage: analytics ? 'granted' : 'denied' });
}

/** Save the visitor's choice and notify listeners. */
export function saveConsent(analytics) {
  const value = { analytics: Boolean(analytics) && !hasGpc(), ts: Date.now() };
  try {
    window.localStorage.setItem(CONSENT_KEY, JSON.stringify(value));
  } catch {
    /* storage blocked: the choice lasts for this page view */
  }
  updateConsentMode(value.analytics);
  try {
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: value }));
  } catch {
    /* old browsers */
  }
  return value;
}

/** Ask the banner to show again (footer "Cookie settings"). */
export function openConsentSettings() {
  try {
    window.dispatchEvent(new CustomEvent(CONSENT_OPEN_EVENT));
  } catch {
    /* old browsers */
  }
}

let gtmLoaded = '';

/**
 * Inject GTM once, only when a valid container id is set, analytics consent was given and the page isn't private.
 * Returns true when GTM is (now) loaded.
 */
export function loadGtm(rawId, pathname = typeof window !== 'undefined' ? window.location.pathname : '/') {
  const id = normalizeGtmId(rawId);
  if (!id || typeof document === 'undefined') return false;
  if (gtmLoaded) return gtmLoaded === id;
  if (isPrivatePath(pathname) || !analyticsAllowed()) return false;
  setConsentDefaults();
  updateConsentMode(true);
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(s);
  gtmLoaded = id;
  return true;
}
